// =================================================================================
// File: src/pages/visitor/DiscoverHomePage.jsx
// Description: Main discovery page for visitors - browse studios, workshops, marketplace
// =================================================================================

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Calendar, ShoppingBag, User, Clock } from "lucide-react";
import { motion } from "framer-motion";
import ProfileSetupModal from "../../components/Auth/ProfileSetupModal";
import VisitorNavBar from "../../components/Common/VisitorNavBar";
import { usePublicStudio } from "../../contexts/PublicStudioContext";
import mudoLogo from "../../assets/mudo-logo-bg.png";

/**
 * DiscoverHomePage - Landing page for visitor experience
 * 
 * Shows various discovery options:
 * - Studios Map
 * - Workshops & Classes
 * - Marketplace
 * - Featured Studios
 */
export default function DiscoverHomePage() {
  const navigate = useNavigate();
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const { allPublicStudios = [], loading: loadingStudios } = usePublicStudio() || {};

  // Animation variants
  const fadeUp = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  };

  const containerStagger = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.15,
      },
    },
  };

  return (
    <div className="relative w-full min-h-screen overflow-hidden font-sans text-white bg-gradient-to-br from-neutral-900 via-neutral-800 to-stone-900">
      {/* Background Video - same as studio home */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="fixed top-0 left-0 w-full h-full object-cover z-0 opacity-40"
        onError={(e) => {
          // Hide video if it fails to load
          e.target.style.display = 'none';
        }}
      >
        <source src="/video/dashboard-bg.mp4" type="video/mp4" />
        <source src="/intro.mp4" type="video/mp4" />
      </video>

      {/* Overlay */}
      <div className="fixed inset-0 z-10 bg-gradient-to-b from-black/40 via-black/20 to-black/40" />

      {/* Content */}
      <div className="relative z-20">
        {/* Navigation Bar with Profile/Logout */}
        <VisitorNavBar showBackButton={false} />

        {/* Hero Section */}
        <motion.div
          className="flex flex-col items-center justify-center min-h-screen px-4 py-20"
          initial="hidden"
          animate="visible"
          variants={containerStagger}
        >
          {/* Icon - Replace Compass with Mudo Logo */}
          <motion.div className="mb-6" variants={fadeUp}>
            <img 
              src={mudoLogo} 
              alt="Mudo Logo" 
              className="w-24 h-24 object-contain"
            />
          </motion.div>

          {/* Title */}
          <motion.h1
            className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 tracking-tight"
            variants={fadeUp}
          >
            Discover Ceramic Studios
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            className="text-xl sm:text-2xl text-white/80 max-w-2xl mb-12 leading-relaxed"
            variants={fadeUp}
          >
            Find studios, workshops, and ceramic spaces around the world. Book directly through Mudo.
          </motion.p>

          {/* Discovery Options */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full max-w-6xl"
            variants={containerStagger}
          >
            {/* Studios Map */}
            <motion.button
              onClick={() => navigate("/discover/map")}
              className="group bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-8 hover:bg-white/15 hover:-translate-y-1 transition-all duration-300 text-left"
              variants={fadeUp}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <MapPin className="w-12 h-12 text-amber-500 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-2xl font-bold mb-3">Studios Map</h3>
              <p className="text-white/70 leading-relaxed">
                Explore ceramic studios on an interactive world map. Find studios near you or discover new ones around the globe.
              </p>
            </motion.button>

            {/* Workshops */}
            <motion.button
              onClick={() => navigate("/discover/workshops")}
              className="group bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-8 hover:bg-white/15 hover:-translate-y-1 transition-all duration-300 text-left"
              variants={fadeUp}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Calendar className="w-12 h-12 text-blue-500 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-2xl font-bold mb-3">Workshops</h3>
              <p className="text-white/70 leading-relaxed">
                Browse upcoming ceramic workshops and classes. Book your spot directly with studios.
              </p>
            </motion.button>

            {/* Book Studio Space */}
            <motion.button
              onClick={() => navigate("/discover/bookings")}
              className="group bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-8 hover:bg-white/15 hover:-translate-y-1 transition-all duration-300 text-left"
              variants={fadeUp}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Clock className="w-12 h-12 text-purple-500 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-2xl font-bold mb-3">Book Studio Space</h3>
              <p className="text-white/70 leading-relaxed">
                Reserve studio space by the hour or day. Access kilns, wheels, and tools at nearby studios.
              </p>
            </motion.button>

            {/* Marketplace */}
            <motion.button
              onClick={() => navigate("/discover/marketplace")}
              className="group bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-8 hover:bg-white/15 hover:-translate-y-1 transition-all duration-300 text-left"
              variants={fadeUp}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <ShoppingBag className="w-12 h-12 text-green-500 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-2xl font-bold mb-3">Marketplace</h3>
              <p className="text-white/70 leading-relaxed">
                Shop ceramic pieces directly from studios. Support independent makers worldwide.
              </p>
            </motion.button>
          </motion.div>

          {/* Switch to Studio Mode CTA */}
          <motion.div
            className="mt-16 bg-gradient-to-r from-orange-600/20 to-red-600/20 backdrop-blur-md border border-orange-500/30 rounded-3xl p-8 text-center max-w-3xl"
            variants={fadeUp}
          >
            <h3 className="text-2xl font-bold mb-4">Run a ceramic studio?</h3>
            <p className="text-white/80 mb-6 leading-relaxed">
              Switch to Studio mode to manage your workshops, bookings, inventory, and production—all in one place.
            </p>
            <motion.button
              onClick={() => {
                try {
                  localStorage.setItem(
                    "mudo:switchToStudioIntentFrom",
                    window.location.pathname + window.location.search
                  );
                } catch {}
                localStorage.setItem("mudo:userRole", "studio");
                navigate("/dashboard");
              }}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-orange-600 to-red-600 text-white font-semibold hover:from-orange-500 hover:to-red-500 transition-all shadow-lg hover:shadow-xl text-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Switch to Studio Mode
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Featured Studios Section */}
        <motion.div
          className="px-4 py-16 bg-black/30 backdrop-blur-sm"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerStagger}
        >
          <motion.h2
            className="text-3xl sm:text-4xl font-bold mb-12 text-center"
            variants={fadeUp}
          >
            Featured Studios
          </motion.h2>
          
          {loadingStudios ? (
            <div className="text-center text-white/60">Loading studios...</div>
          ) : allPublicStudios.length === 0 ? (
            <div className="text-center text-white/60 max-w-md mx-auto">
              <p className="mb-4">No studios are currently featured.</p>
              <p className="text-sm">Studios will appear here when they enable "Show on MudoMap" in their public profile settings.</p>
            </div>
          ) : (
            <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {allPublicStudios.map((studio) => (
                  <motion.div
                    key={studio.id}
                    onClick={() => navigate(`/discover/studios/${studio.studio_id}`)}
                    className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all duration-300 cursor-pointer"
                    variants={fadeUp}
                    whileHover={{ scale: 1.03, y: -4 }}
                  >
                  {/* Studio Avatar/Cover - public_studios doesn't have cover_image */}
                  <div className="w-full h-32 rounded-xl mb-4 overflow-hidden">
                    <div 
                      className="w-full h-full bg-gradient-to-br from-amber-500/30 to-orange-500/30"
                    />
                  </div>
                  
                  {/* Studio Info - use fields from public_studios table */}
                  <h4 className="font-semibold mb-2 text-lg truncate">
                    {studio.display_name || 'Unnamed Studio'}
                  </h4>
                  <p className="text-sm text-white/60 truncate">
                    {studio.city && studio.country 
                      ? `${studio.city}, ${studio.country}` 
                      : studio.address || 'Location not set'}
                  </p>
                  
                  {/* Show tags if available */}
                  {studio.tags && studio.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {studio.tags.slice(0, 2).map((tag, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-white/10 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Profile Setup Modal */}
      <ProfileSetupModal 
        isOpen={profileModalOpen} 
        onComplete={() => setProfileModalOpen(false)} 
      />
    </div>
  );
}
